import axios from 'axios';
import {apiService} from "./api.service";
import {convertToPageable, downloadFileWithPresignedUrl, extractData, handleApiException, handleResponse} from "../helpers/apiResponseHelper";
import {AgreementSigningStatus, IAgreement, IAgreementRequest} from "../models/agreements";

async function getAgreements(customerId: number, pageRequest: IAgreementRequest) {
    return axios.get(apiService.getApiUrl() + '/agreements/' + customerId,
        {
            params: pageRequest
        }).then(handleResponse).then(convertToPageable).catch(handleApiException);
}

async function getAgreementFile(agreementId: number, type: 'PDF' | 'BDOC' = 'PDF') {
    return axios.get(`${apiService.getApiUrl()}/agreements/file?agreementId=${agreementId}&type=${type}`)
        .then(handleResponse).catch(handleApiException);
}

async function getAgreementFileWithDownload(agreementId: number, type: 'PDF' | 'BDOC' = 'PDF') {
    return getAgreementFile(agreementId, type)
        .then((res) => downloadFileWithPresignedUrl(res.data));
}

async function getAgreementSignatures(agreementId: number) {
    return axios.get(apiService.getApiUrl() + '/agreements/' + agreementId + '/signatures')
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}

async function initIdCardSigning(customerId: number, agreementId: number, certHash: string, contactPersonId: number) {
    return axios.post(apiService.getApiUrl() + '/agreements/' + customerId + '/sign-init',
        {
            agreementId,
            certHash
        }, {
            params: { contactPersonId }
        })
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}
async function signIdCardAgreement(customerId: number, agreementId: number, signatureHash: string, contactPersonId: number) {
    return axios.post(apiService.getApiUrl() + '/agreements/' + customerId + '/sign-finalize',
        {
            agreementId,
            signatureHash
        }, {
            params: { contactPersonId }
        })
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}

async function initMobileIdSigning(customerId: number, agreementId: number, nationalIdentityCode: string, phoneNumber: string, contactPersonId: number) {
    return axios.post(apiService.getApiUrl() + '/agreements/' + customerId + '/sign-mobileid',
        {
            agreementId,
            nationalIdentityCode,
            phoneNumber
        }, {
            params: { contactPersonId }
        })
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}

async function pollMobileIdSigning(customerId: number, agreementId: number, nationalIdentityCode: string, verificationCode: string, contactPersonId: number) {
    return axios.post(apiService.getApiUrl() + '/agreements/' + customerId + '/sign-mobileid-poll',
        {
            agreementId,
            nationalIdentityCode,
            verificationCode
        }, {
            params: { contactPersonId }
        })
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}

async function initSmartIdSigning(customerId: number, agreementId: number, nationalIdentityCode: string, contactPersonId: number) {
    return axios.post(apiService.getApiUrl() + '/agreements/' + customerId + '/sign-smartid',
        {
            agreementId,
            nationalIdentityCode,
        },
        {
            params: { contactPersonId }
        })
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}

async function pollSmartIdSigning(customerId: number, agreementId: number, nationalIdentityCode: string, verificationCode: string, contactPersonId: number) {
    return axios.post(apiService.getApiUrl() + '/agreements/' + customerId + '/sign-smartid-poll',
        {
            agreementId,
            nationalIdentityCode,
            verificationCode
        },
        {
            params: { contactPersonId }
        })
        .then(handleResponse)
        .then(extractData)
        .catch(handleApiException)
}

function getSigningStatus(agreement: IAgreement): AgreementSigningStatus {
    if (agreement.isSignedByCustomer && agreement.isSignedByVendor) {
        return AgreementSigningStatus.COMPLETED;
    }
    if (agreement.isSignedByCustomer && !agreement.isSignedByVendor) {
        return AgreementSigningStatus.PENDING_VENDOR;
    }
    if (!agreement.isSignedByCustomer && agreement.isSignedByVendor) {
        return AgreementSigningStatus.PENDING_CUSTOMER;
    }
    return AgreementSigningStatus.PREPARED;
}

const AgreementsService = {
    getAgreements,
    getAgreementFile,
    getAgreementFileWithDownload,
    getSigningStatus,
    initIdCardSigning,
    signIdCardAgreement,
    initMobileIdSigning,
    pollMobileIdSigning,
    getAgreementSignatures,
    initSmartIdSigning,
    pollSmartIdSigning
};

export default AgreementsService;
